import React from 'react';
import type { IUserGetTransfersResponse as TransfersDTO } from '@/app-package';
import { Modal, ModalProps, Tabs, TabPane, Button } from 'components';
import { AuthContext } from 'contexts';
import UserInfoTable from './UserInfoTable';
import DetailsTable from './DetailsTable';
import ActivityTable from './ActivityTable';
import { StyledTabBar } from './StyledComponents';
import TransferHistoryTable from './TransferHistoryTable';
import { UserHistoryModalType } from './constants';

export enum UserTab {
  DETAILS = 'DETAILS',
  ACTIVITY = 'ACTIVITY',
  TRANSFER = 'TRANSFER',
}

const TabNames: Record<UserTab, string> = {
  DETAILS: 'Details',
  ACTIVITY: 'Activity',
  TRANSFER: 'Transfer',
};

interface SharedHistoryModalProps extends ModalProps {
  isModalVisible: boolean;
  handleCancel: () => void;
  tabsToDisplay?: UserTab[];
  type?: UserHistoryModalType;
}

export interface UserHistoryModalProps extends SharedHistoryModalProps {
  transfer?: TransfersDTO['transfers'][number] | undefined;
  userId: string;
  type?: UserHistoryModalType.USER;
}

export interface TransferHistoryModalProps extends SharedHistoryModalProps {
  userId?: undefined;
  transfer: TransfersDTO['transfers'][number];
  type: UserHistoryModalType.TRANSFER;
  tabsToDisplay: [UserTab.TRANSFER];
}

export const isTransferType = (
  props: UserHistoryModalProps | TransferHistoryModalProps,
): props is TransferHistoryModalProps =>
  props.type === UserHistoryModalType.TRANSFER;

const UserHistoryModal = (
  props: UserHistoryModalProps | TransferHistoryModalProps,
) => {
  const { auth } = React.useContext(AuthContext);
  const {
    isModalVisible,
    handleCancel,
    userId,
    transfer,
    type = UserHistoryModalType.USER,
    tabsToDisplay = type === UserHistoryModalType.USER
      ? Object.values(UserTab)
      : [UserTab.TRANSFER],
    ...rest
  } = props;

  return (
    <Modal
      title="User History"
      open={isModalVisible}
      onCancel={handleCancel}
      cancelText="Close"
      footer={[
        <Button key="Close" type="ghost" onClick={handleCancel}>
          Close
        </Button>,
      ]}
      width={750}
      {...rest}
    >
      <UserInfoTable userId={userId} transfer={transfer} type={type} />
      <Tabs
        defaultActiveKey={UserTab.DETAILS}
        renderTabBar={(defaultProps, Default) => (
          <StyledTabBar>
            <Default {...defaultProps} />
          </StyledTabBar>
        )}
      >
        {/* Accessible to branch admins or higher */}
        {tabsToDisplay.includes(UserTab.DETAILS) && auth.isAdmin && (
          <TabPane tab={TabNames.DETAILS} key={UserTab.DETAILS}>
            <DetailsTable userId={userId} />
          </TabPane>
        )}
        {/* Only accessible to global admins */}
        {tabsToDisplay.includes(UserTab.ACTIVITY) &&
          auth.roles[auth.role] >= auth.roles.GLOBAL_ADMIN && (
            <TabPane tab={TabNames.ACTIVITY} key={UserTab.ACTIVITY}>
              <ActivityTable userId={userId} />
            </TabPane>
          )}
        {/* Accessible to branch admins or higher */}
        {tabsToDisplay.includes(UserTab.TRANSFER) && auth.isAdmin && (
          <TabPane tab={TabNames.TRANSFER} key={UserTab.TRANSFER}>
            <TransferHistoryTable
              transferId={transfer?.id}
              userId={userId}
              type={type}
            />
          </TabPane>
        )}
      </Tabs>
    </Modal>
  );
};

export default UserHistoryModal;
